import { closeAiNotesPanel, openAiNotesPanel } from '../ai-notes/actions';
import { OPEN_CHAT } from '../chat/actionTypes';
import { closeChat, setFocusedTab } from '../chat/actions.any';
import { ChatTabs } from '../chat/constants';
import { isChatDisabled } from '../chat/functions';
import { close as closeCustomPanel, open as openCustomPanel } from '../custom-panel/actions.web';
import { close as closeParticipantsPane, open as openParticipantsPane } from '../participants-pane/actions.web';
import { BRING_FLOATING_SIDE_PANEL_TO_FRONT, CLOSE_ALL_SIDE_PANELS, DOCK_SIDE_PANEL_TAB, OPEN_SIDE_PANEL_TAB, POP_OUT_SIDE_PANEL_TAB, REMOVE_SIDE_PANEL_TAB, SET_ACTIVE_SIDE_PANEL_TAB, SET_FLOATING_SIDE_PANEL_GEOMETRY, SET_SIDE_PANEL_IS_RESIZING, SET_SIDE_PANEL_WIDTH, SET_USER_SIDE_PANEL_WIDTH } from './actionTypes';
import { SIDE_PANEL_FEATURE_KEY, SidePanelTabId } from './constants';
import { createDefaultFloatingGeometry, isSidePanelPopoutEnabled, shouldUseSidePanelBottomDrawer } from './functions';
import logger from './logger';
/**
 * Open (or focus) a docked side panel tab in Redux state.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Object}
 */
export function openSidePanelTab(tabId) {
    return {
        type: OPEN_SIDE_PANEL_TAB,
        tabId
    };
}
/**
 * Remove a tab from docked and floating lists.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Object}
 */
export function removeSidePanelTab(tabId) {
    return {
        type: REMOVE_SIDE_PANEL_TAB,
        tabId
    };
}
/**
 * Set the active docked tab.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Object}
 */
export function setActiveSidePanelTab(tabId) {
    return {
        type: SET_ACTIVE_SIDE_PANEL_TAB,
        tabId
    };
}
/**
 * Clear all side panel tabs (state only; prefer {@link closeAllSidePanels}).
 *
 * @returns {Object}
 */
export function clearAllSidePanelsState() {
    return {
        type: CLOSE_ALL_SIDE_PANELS
    };
}
/**
 * Pop a docked tab into a floating window.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @param {IFloatingGeometry} geometry - Initial geometry.
 * @returns {Object}
 */
export function popOutSidePanelTab(tabId, geometry) {
    return {
        type: POP_OUT_SIDE_PANEL_TAB,
        tabId,
        geometry
    };
}
/**
 * Dock a floating tab back into the side panel.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Object}
 */
export function dockSidePanelTab(tabId) {
    return {
        type: DOCK_SIDE_PANEL_TAB,
        tabId
    };
}
/**
 * Update floating window geometry.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @param {Partial<IFloatingGeometry>} geometry - Geometry patch.
 * @returns {Object}
 */
export function setFloatingSidePanelGeometry(tabId, geometry) {
    return {
        type: SET_FLOATING_SIDE_PANEL_GEOMETRY,
        tabId,
        geometry
    };
}
/**
 * Bring a floating window to the front.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Object}
 */
export function bringFloatingSidePanelToFront(tabId) {
    return {
        type: BRING_FLOATING_SIDE_PANEL_TO_FRONT,
        tabId
    };
}
/**
 * Set docked side panel width.
 *
 * @param {number} width - Width in pixels.
 * @returns {Object}
 */
export function setSidePanelWidth(width) {
    return {
        type: SET_SIDE_PANEL_WIDTH,
        width
    };
}
/**
 * Set user-preferred docked side panel width.
 *
 * @param {number} width - Width in pixels.
 * @returns {Object}
 */
export function setUserSidePanelWidth(width) {
    return {
        type: SET_USER_SIDE_PANEL_WIDTH,
        width
    };
}
/**
 * Set whether the docked side panel is being resized.
 *
 * @param {boolean} resizing - Resizing flag.
 * @returns {Object}
 */
export function setSidePanelIsResizing(resizing) {
    return {
        type: SET_SIDE_PANEL_IS_RESIZING,
        resizing
    };
}
/**
 * Open the underlying feature for a tab id.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Function}
 */
export function openSidePanelFeature(tabId) {
    return (dispatch) => {
        switch (tabId) {
            case SidePanelTabId.CHAT:
                dispatch(setFocusedTab(ChatTabs.CHAT));
                dispatch({ type: OPEN_CHAT });
                break;
            case SidePanelTabId.PARTICIPANTS:
                dispatch(openParticipantsPane());
                break;
            case SidePanelTabId.AI_NOTES:
                dispatch(openAiNotesPanel());
                break;
            case SidePanelTabId.CUSTOM_PANEL:
                dispatch(openCustomPanel());
                break;
            case SidePanelTabId.POLLS:
            case SidePanelTabId.CLOSED_CAPTIONS:
            case SidePanelTabId.FILE_SHARING:
                logger.info('打开独立侧栏应用', { tabId });
                dispatch(openSidePanelTab(tabId));
                break;
        }
    };
}
/**
 * Close the underlying feature for a tab id.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Function}
 */
export function closeSidePanelFeature(tabId) {
    return (dispatch) => {
        switch (tabId) {
            case SidePanelTabId.CHAT:
                dispatch(closeChat());
                break;
            case SidePanelTabId.PARTICIPANTS:
                dispatch(closeParticipantsPane());
                break;
            case SidePanelTabId.AI_NOTES:
                dispatch(closeAiNotesPanel());
                break;
            case SidePanelTabId.CUSTOM_PANEL:
                dispatch(closeCustomPanel());
                break;
            case SidePanelTabId.POLLS:
            case SidePanelTabId.CLOSED_CAPTIONS:
            case SidePanelTabId.FILE_SHARING:
                logger.info('关闭独立侧栏应用', { tabId });
                dispatch(removeSidePanelTab(tabId));
                break;
        }
    };
}
/**
 * Close a side panel tab (docked or floating) via its feature close action.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Function}
 */
export function closeSidePanelTab(tabId) {
    return closeSidePanelFeature(tabId);
}
/**
 * Close every docked and floating side panel app.
 *
 * @returns {Function}
 */
export function closeAllSidePanels() {
    return (dispatch, getState) => {
        const { openTabs, floatingTabs } = getState()[SIDE_PANEL_FEATURE_KEY];
        const tabs = Array.from(new Set([...openTabs, ...floatingTabs]));
        tabs.forEach(tabId => {
            dispatch(closeSidePanelFeature(tabId));
        });
        dispatch(clearAllSidePanelsState());
    };
}
/** 顶栏「隐藏侧边栏」时记住上次 dock 页签，展开时还原。 */
let lastDockedSidePanelTabs = [];
/**
 * 顶栏隐藏/展开 dock 侧栏：有打开页签则全部关闭并记住；否则还原上次页签（无记录则打开聊天）。
 *
 * @returns {Function}
 */
export function toggleDockedSidePanel() {
    return (dispatch, getState) => {
        const { openTabs } = getState()[SIDE_PANEL_FEATURE_KEY];
        if (openTabs.length > 0) {
            lastDockedSidePanelTabs = [...openTabs];
            logger.info('顶栏隐藏侧边栏', { tabIds: lastDockedSidePanelTabs });
            dispatch(closeAllSidePanels());
            return;
        }
        const restoreTabs = lastDockedSidePanelTabs.length
            ? lastDockedSidePanelTabs
            : [isChatDisabled(getState()) ? SidePanelTabId.PARTICIPANTS : SidePanelTabId.CHAT];
        logger.info('顶栏展开侧边栏', { tabIds: restoreTabs });
        restoreTabs.forEach(tabId => {
            dispatch(openSidePanelFeature(tabId));
        });
    };
}
/**
 * Toggle / focus a side panel app from toolbar buttons.
 *
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {Function}
 */
export function toggleSidePanelApp(tabId) {
    return (dispatch, getState) => {
        const { openTabs, activeTab, floatingTabs } = getState()[SIDE_PANEL_FEATURE_KEY];
        if (floatingTabs.includes(tabId)) {
            dispatch(bringFloatingSidePanelToFront(tabId));
            return;
        }
        if (openTabs.includes(tabId) && activeTab === tabId) {
            dispatch(closeSidePanelTab(tabId));
            return;
        }
        // 手机端每个应用是独立底部抽屉：先打开目标，再关掉其它 docked 页签，避免抽屉闪断。
        if (shouldUseSidePanelBottomDrawer()) {
            const others = openTabs.filter(openId => openId !== tabId);
            if (openTabs.includes(tabId)) {
                dispatch(setActiveSidePanelTab(tabId));
            }
            else {
                dispatch(openSidePanelFeature(tabId));
            }
            if (others.length > 0) {
                logger.info('手机端底部抽屉互斥切换，关闭其它侧栏应用', {
                    nextTabId: tabId,
                    closingTabIds: others
                });
                others.forEach(openId => {
                    dispatch(closeSidePanelTab(openId));
                });
            }
            return;
        }
        if (openTabs.includes(tabId)) {
            dispatch(setActiveSidePanelTab(tabId));
            return;
        }
        dispatch(openSidePanelFeature(tabId));
    };
}
/**
 * 打开侧栏应用并以独立浮动窗展示（若已浮动则前置）。
 *
 * @param {SidePanelTabId} tabId - 应用 id（chat / participants 等）。
 * @returns {Function}
 */
export function openSidePanelAsPopout(tabId) {
    return (dispatch, getState) => {
        const state = getState();
        const { floatingTabs, openTabs } = state[SIDE_PANEL_FEATURE_KEY];
        if (floatingTabs.includes(tabId)) {
            dispatch(bringFloatingSidePanelToFront(tabId));
            logger.info('侧栏独立窗口已打开，前置到最前', { tabId });
            return;
        }
        if (!openTabs.includes(tabId)) {
            dispatch(openSidePanelFeature(tabId));
        }
        // 手机端无浮动窗，仅打开对应面板。
        if (shouldUseSidePanelBottomDrawer() || !isSidePanelPopoutEnabled(getState())) {
            logger.info('当前视口不支持侧栏独立窗口，改为打开面板', { tabId });
            return;
        }
        const geometry = createDefaultFloatingGeometry(getState());
        dispatch(popOutSidePanelTab(tabId, geometry));
        logger.info('已打开侧栏独立窗口', { tabId, geometry });
    };
}
